/**
 * userActivity — the ONE record of whether a person is using this page, and the background-request
 * pause it drives (UNREACHABLE-MODULE-BURNDOWN-001 unit D2; owner ruling D-1 "an idle admin
 * re-authenticates"; coordinator ruling on D2-Q1: background polling is NOT activity).
 *
 * ## Why it exists
 *
 * A platform session ends after 30 minutes without a refresh (`PLATFORM_SESSION_POLICY` in
 * `@puffin-core/auth-routes`). Every API call made after the 10-minute access token lapses triggers
 * a refresh — so a console tab left open with a status poller running refreshed itself every ten
 * minutes and stayed signed in to the 4-hour cap with nobody at the keyboard. NIST's inactivity is
 * the PERSON's inactivity, so the session layer has to know whether a request came from a person
 * and whether a person has been here.
 *
 * ## What counts as activity
 *
 * A real interaction with the page: `pointerdown`, `keydown` or `input` ({@link USER_ACTIVITY_EVENTS},
 * captured at the document). A tab becoming visible is NOT activity on its own — only a request the
 * user then makes is, and a user-initiated request refreshes regardless of this record.
 *
 * ## The rule it serves (`createApiClient`)
 *
 * - Every request records the access token it presents ({@link observeToken}); a token's first use
 *   is the page-local "last refresh".
 * - A `{ background: true }` request whose token 401s may refresh ONLY if the user has interacted
 *   since that token was first used ({@link mayRefreshInBackground}). Otherwise the token is paused
 *   ({@link pauseBackground}) and every later background request presenting it is declined locally
 *   ({@link isBackgroundPaused}) — no network, no refresh.
 * - The pause lifts when the token changes (a user request refreshed it — here or in another tab
 *   sharing the store) or when the user interacts again.
 *
 * @module @puffin-core/api-client/userActivity
 */

/** The DOM events that are a person using the page. */
export const USER_ACTIVITY_EVENTS = Object.freeze(['pointerdown', 'keydown', 'input']);

/** Capture-phase + passive: seen before any component can stop propagation, never delays input. */
const LISTENER_OPTIONS = Object.freeze({ capture: true, passive: true });

/**
 * @param {Object} [options]
 * @param {EventTarget} [options.target] - where interactions are observed (the page's `document`);
 *   absent in a runtime with no DOM, where no interaction is ever recorded
 * @param {() => number} [options.now=Date.now] - the clock (ms)
 * @returns {Readonly<{
 *   start: () => boolean, stop: () => void, recordInteraction: () => void,
 *   observeToken: (token: string) => void, mayRefreshInBackground: (token: string) => boolean,
 *   pauseBackground: (token: string) => void, isBackgroundPaused: (token: string) => boolean,
 * }>}
 */
export function createUserActivityMonitor({ target, now = Date.now } = {}) {
  let lastInteractionAt = Number.NEGATIVE_INFINITY;
  let observedToken = null;
  let observedAt = 0;
  let pausedToken = null;
  let pausedAt = 0;
  let listening = false;

  const recordInteraction = () => {
    lastInteractionAt = now();
  };

  return Object.freeze({
    /**
     * Begin observing interactions on `target`. Idempotent.
     *
     * @returns {boolean} whether the monitor is listening
     */
    start() {
      if (!listening && target && typeof target.addEventListener === 'function') {
        for (const type of USER_ACTIVITY_EVENTS) {
          target.addEventListener(type, recordInteraction, LISTENER_OPTIONS);
        }
        listening = true;
      }
      return listening;
    },

    /** Stop observing. Idempotent. */
    stop() {
      if (listening) {
        for (const type of USER_ACTIVITY_EVENTS) {
          target.removeEventListener(type, recordInteraction, LISTENER_OPTIONS);
        }
        listening = false;
      }
    },

    recordInteraction,

    /**
     * Note the access token a request is about to present. The first time a token is seen is the
     * page-local moment of the refresh (or sign-in) that minted it.
     *
     * @param {string} token
     */
    observeToken(token) {
      if (token !== observedToken) {
        observedToken = token;
        observedAt = now();
      }
    },

    /**
     * May a background request that 401'd with `token` refresh the session? Only when `token` is the
     * one in use and a person has interacted with the page since it was first used.
     *
     * @param {string} token
     * @returns {boolean}
     */
    mayRefreshInBackground(token) {
      return token === observedToken && lastInteractionAt > observedAt;
    },

    /**
     * Pause background requests presenting `token` — it expired with nobody here.
     *
     * @param {string} token
     */
    pauseBackground(token) {
      pausedToken = token;
      pausedAt = now();
    },

    /**
     * Is a background request presenting `token` paused? Yes while it is the paused token and the
     * user has not interacted since the pause.
     *
     * @param {string} token
     * @returns {boolean}
     */
    isBackgroundPaused(token) {
      return pausedToken !== null && token === pausedToken && !(lastInteractionAt > pausedAt);
    },
  });
}

let pageMonitor = null;

/**
 * The page's monitor — one per document, shared by every session client on the page, started on
 * first use. In a runtime with no `document` it records no interaction, so a background request
 * there never refreshes.
 *
 * @returns {ReturnType<typeof createUserActivityMonitor>}
 */
export function getPageActivityMonitor() {
  if (!pageMonitor) {
    pageMonitor = createUserActivityMonitor({
      target: typeof document === 'undefined' ? undefined : document,
    });
    pageMonitor.start();
  }
  return pageMonitor;
}
